import { Tooltip, useMediaQuery } from '@mui/material';
import { TransitionGroup } from 'react-transition-group';

import { JobTagChip } from './job-tag-chip';
import { JOB_TAG_TRANSITION_MS, JobTagTransition } from './job-tag-transition';

interface JobTagChipListProps {
  tags: readonly { _id: string; name: string; icon: string }[];
  /** When set, each chip gets a `×` that calls this with its tag's id. */
  onRemove?: (tagId: string) => void;
  /**
   * The flex gap of the row the chips sit in, in theme spacing units. The
   * list has to know it to close the gap along with a chip — see
   * `JobTagTransition`.
   */
  gap: number;
  /** Shown on hover of each chip. */
  tooltip?: string;
}

/**
 * A job's tags as chips that ease in when added and out when removed.
 *
 * Renders NO wrapper of its own — the chips are children of whatever flex row
 * this is placed in, so in the job header they wrap one by one alongside the
 * customer and date chips rather than as a block.
 *
 * Tags already there when the list mounts don't animate; only changes do.
 * @param root0
 * @param root0.tags
 * @param root0.onRemove
 * @param root0.gap
 * @param root0.tooltip
 */
export function JobTagChipList({
  tags,
  onRemove = undefined,
  gap,
  tooltip = undefined,
}: JobTagChipListProps) {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const timeout = reduceMotion ? 0 : JOB_TAG_TRANSITION_MS;

  return (
    <TransitionGroup component={null}>
      {tags.map((tag) => {
        const chip = (
          <JobTagChip
            tag={tag}
            onDelete={onRemove ? () => onRemove(tag._id) : undefined}
          />
        );
        return (
          <JobTagTransition key={tag._id} gap={gap} timeout={timeout}>
            {tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip}
          </JobTagTransition>
        );
      })}
    </TransitionGroup>
  );
}
